<script setup lang="ts">
import { ECheckpointActions } from '@/store/checkpointStore/CheckpointStoreTypes'
import { EStoreModules } from '@/store/storeType'
import { EHubProvider } from '@/types/enum/HubProviderEnum'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { PartnerModel } from '@/types/partners/PartnerModel'
import { ECheckpointsPerm } from '@/types/permissions/CheckpointsPermissionsEnum'
import type { PermissionsEnums } from '@/types/permissions/PermissionsEnum'
import CheckpointClearDetectionsAction from '@/views/CheckpointsView/Detail/components/CheckpointClearDetectionsAction.vue'
import {
    BaseDialog,
    DeleteButton,
    EditableButtonGroup,
    InformationBaseCard,
    PermissionWrapper,
    StackLayout
} from '@rfc/rfc-components-library'
import type { DeleteButtonConfirmationDialog } from '@rfc/rfc-components-library/types'
import { useWindowSize } from '@vueuse/core'
import { isEqual } from 'lodash'
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import CheckpointAssociateDevice from './Detail/CheckpointAssociateDevice.vue'
import CheckpointDetailInformations from './Detail/CheckpointDetailInformations.vue'
import CheckpointPartnerInformations from './Detail/CheckpointPartnerInformations.vue'

const store = useStore()

type Props = {
    sync?: boolean
    checkpoint: CheckpointModel
    partnerConfig?: PartnerModel
    open: boolean
    deletePermission?: PermissionsEnums
    loading: boolean
}
const props = defineProps<Props>()
type Emits = {
    (e: 'refresh'): void
    (e: 'refreshAssociate'): void
    (e: 'close'): void
    (e: 'delete', value: CheckpointModel): void
}
const emit = defineEmits<Emits>()

const route = useRoute()
const { t } = useI18n()
const eventId = computed(() => parseInt(route.params.event_id.toString()))
const storedCheckpoint = computed(() => store?.state.checkpoint)
const isViewerOnly = inject<boolean>(EHubProvider.IS_VIEWER_ONLY, false)

const data = ref<CheckpointModel>({ ...props.checkpoint })
const editMode = ref(!isViewerOnly)
watch(
    () => isViewerOnly,
    newValue => (editMode.value = newValue)
)

const getCheckpoint = () =>
    store
        ?.dispatch(`${EStoreModules.CHECKPOINT}/${ECheckpointActions.FETCH_CHECKPOINT}`, {
            eventId: eventId.value,
            checkpointId: props.checkpoint?.id
        })
        .then(res => (data.value = { ...res }))

watch(
    () => props.checkpoint,
    () => getCheckpoint(),
    { immediate: true }
)

const save = () => {
    if (!isEqual(data.value, storedCheckpoint.value?.data?.checkpoint)) {
        store
            ?.dispatch(`${EStoreModules.CHECKPOINT}/${ECheckpointActions.UPDATE_CHECKPOINT}`, {
                eventId: eventId.value,
                checkpointId: props.checkpoint?.id,
                data: data.value
            })
            .then(() => {
                refreshCheckpoint()
                emit('close')
            })
    } else {
        emit('close')
    }
}

const handleCancel = () => {
    data.value = { ...props.checkpoint }
    emit('close')
}

const refreshCheckpoint = () => {
    getCheckpoint()
    emit('refresh')
}

const { width } = useWindowSize()
const isMobile = computed(() => width.value < 768)

const confirmationDialog: DeleteButtonConfirmationDialog = {
    title: t('checkpoint.actions.deleteCheckpoint.deleteCheckpointTitle'),
    contentText: t('checkpoint.actions.deleteCheckpoint.deleteCheckpointContent')
}
</script>

<template>
    <BaseDialog
        :open="open"
        :title="$t('event.detail.checkpoints.createCheckpointTitle')"
        :loading="!!store?.state.checkpoint.loading"
        @close="handleCancel"
        @confirm="save"
        :fullscreen="isMobile"
        :class="`md:w-full lg:w-1/2 ${isMobile ? 'checkpoint-dialog-mobile' : ''}`"
        :contentClass="`checkpoint-dialog `">
        <template #content>
            <StackLayout direction="column" :gap="16" isResponsive class="h-full">
                <PermissionWrapper
                    v-if="!sync && !isViewerOnly"
                    :permission="ECheckpointsPerm.CHECKPOINTS_EDIT">
                    <StackLayout
                        direction="row"
                        class="checkpoint-informations place-content-end flex-wrap w-full"
                        :gap="8">
                        <CheckpointClearDetectionsAction :data="data" />
                        <EditableButtonGroup
                            v-if="!isViewerOnly"
                            @edit="editMode = $event"
                            @save="save" />
                    </StackLayout>
                </PermissionWrapper>
                <StackLayout direction="column" :gap="16" class="h-full justify-center">
                    <StackLayout direction="row" class="checkpoint-informations" :gap="8">
                        <CheckpointDetailInformations
                            :data="data"
                            :editMode="editMode"
                            @change="data = { ...data, ...$event }" />
                        <InformationBaseCard
                            v-if="partnerConfig"
                            :label="$t('checkpoint.title.partnerTitle')"
                            class="w-full">
                            <CheckpointPartnerInformations
                                :partnerConfig="partnerConfig"
                                :partnerConfigValue="data.partner_config"
                                :editMode="editMode"
                                @change="data = { ...data, ...$event }"
                                direction="column"
                                class="w-full"
                                :gap="8" />
                        </InformationBaseCard>
                    </StackLayout>
                    <CheckpointAssociateDevice
                        v-if="!isViewerOnly"
                        :associates="data?.associates ?? []"
                        :checkpointId="data.id"
                        :editMode="editMode"
                        @refresh="refreshCheckpoint" />
                </StackLayout>
            </StackLayout>
        </template>
        <template #left-action>
            <PermissionWrapper :permission="deletePermission">
                <DeleteButton
                    v-if="data"
                    :loading="loading"
                    :confirmationDialog="confirmationDialog"
                    :label="$t('checkpoint.buttons.deleteCheckpoint')"
                    @click="$emit('delete', data)" />
            </PermissionWrapper>
        </template>
    </BaseDialog>
</template>

<style scoped lang="scss">
.checkpoint-dialog {
    padding: 1rem;
}
</style>
